import React, { useCallback, useEffect, useMemo, useState } from 'react'
import { Link, useNavigate } from 'react-router-dom'
import { Bell, Check, MoreHorizontal, Settings, Trash2 } from 'lucide-react'
import { Menu, MenuItem, MenuSeparator } from '../ui'
import { useNotifications } from '../../contexts/NotificationContext'
import { useWatchlist } from '../../contexts/WatchlistContext'
import {
  deleteNotification,
  fetchNotifications,
  isUnread,
} from '../../services/notifications'
import {
  findWatchlistItem,
  type WatchlistEntityType,
  type WatchlistItem,
} from '../../services/watchlist'
import type { AppNotification } from '../../types/notifications'
import {
  notificationEntityLabel,
} from '../../utils/notificationLabels'
import { notificationHref } from '../../utils/notificationLinks'
import { notificationSections } from '../../utils/notificationPresentation'
import { useToast } from '../../contexts/ToastContext'
import { usePageTitle } from '../../hooks/usePageTitle'
import { Container } from '../ui'
import NotificationItemContent from './NotificationItemContent'
import WatchlistItemNotificationModal from './WatchlistItemNotificationModal'

const PAGE_SIZE = 50

interface NotificationsInboxProps {
  /** When true, omits outer page chrome (used inside /account). */
  embedded?: boolean
}

function inboxSummary(total: number, unreadCount: number): string {
  if (total === 0) return 'Ingen notifikationer'
  if (unreadCount === 0) return `${total} i alt · alle læst`
  if (unreadCount === total) {
    return total === 1 ? '1 ulæst' : `${unreadCount} ulæste`
  }
  return `${unreadCount} ulæst${unreadCount === 1 ? '' : 'e'} · ${total} i alt`
}

const NotificationsInbox: React.FC<NotificationsInboxProps> = ({ embedded = false }) => {
  const { markRead, markAllRead, refresh: refreshBadge } = useNotifications()
  const { data: watchlistData, updateItemNotificationPreferences } = useWatchlist()
  const { showToast } = useToast()
  usePageTitle(embedded ? undefined : 'Notifikationer')

  const [items, setItems] = useState<AppNotification[]>([])
  const [total, setTotal] = useState(0)
  const [unreadCount, setUnreadCount] = useState(0)
  const [loading, setLoading] = useState(true)
  const [busy, setBusy] = useState(false)
  const [settingsItem, setSettingsItem] = useState<WatchlistItem | null>(null)

  const load = useCallback(async () => {
    setLoading(true)
    try {
      const data = await fetchNotifications(PAGE_SIZE, 0, false)
      setItems(data.items)
      setTotal(data.total)
      setUnreadCount(data.unreadCount)
    } catch (err: unknown) {
      showToast(err instanceof Error ? err.message : 'Fejl', { tone: 'signal' })
    } finally {
      setLoading(false)
    }
  }, [showToast])

  useEffect(() => {
    void load()
  }, [load])

  const sections = useMemo(() => notificationSections(items), [items])

  async function handleMarkRead(n: AppNotification) {
    if (!isUnread(n)) return
    try {
      await markRead(n.id)
      setItems((prev) =>
        prev.map((row) =>
          row.id === n.id ? { ...row, readAt: new Date().toISOString() } : row
        )
      )
      setUnreadCount((c) => Math.max(0, c - 1))
      await refreshBadge()
    } catch (err: unknown) {
      showToast(err instanceof Error ? err.message : 'Fejl', { tone: 'signal' })
    }
  }

  async function handleMarkAll() {
    setBusy(true)
    try {
      await markAllRead()
      setItems((prev) =>
        prev.map((n) => ({ ...n, readAt: n.readAt ?? new Date().toISOString() }))
      )
      setUnreadCount(0)
      showToast('Alle notifikationer markeret som læst.')
    } catch (err: unknown) {
      showToast(err instanceof Error ? err.message : 'Fejl', { tone: 'signal' })
    } finally {
      setBusy(false)
    }
  }

  async function handleRemove(n: AppNotification) {
    try {
      await deleteNotification(n.id)
      const wasUnread = isUnread(n)
      setItems((prev) => prev.filter((row) => row.id !== n.id))
      setTotal((t) => Math.max(0, t - 1))
      if (wasUnread) setUnreadCount((c) => Math.max(0, c - 1))
      await refreshBadge()
      showToast('Notifikation fjernet.')
    } catch (err: unknown) {
      showToast(err instanceof Error ? err.message : 'Fejl', { tone: 'signal' })
    }
  }

  const summary = inboxSummary(total, unreadCount)

  const content = (
    <>
      {!embedded && (
        <div className="flex items-start justify-between gap-4 mb-4">
          <div>
            <p className="font-mono text-xs uppercase tracking-widest text-signal mb-1.5">
              Indbakke
            </p>
            <h1 className="font-serif text-2xl text-ink">Notifikationer</h1>
            {!loading && <p className="text-sm text-ink-500 mt-1">{summary}</p>}
          </div>
          {unreadCount > 0 && (
            <button
              type="button"
              disabled={busy}
              onClick={() => void handleMarkAll()}
              className="shrink-0 text-xs uppercase tracking-wide text-signal hover:text-signal-700 disabled:opacity-50"
            >
              Markér alle læst
            </button>
          )}
        </div>
      )}

      {embedded && (
        <div className="flex items-center justify-between gap-3 mb-3">
          {!loading && (
            <p className="text-sm text-ink-500">{summary}</p>
          )}
          {unreadCount > 0 && (
            <button
              type="button"
              disabled={busy}
              onClick={() => void handleMarkAll()}
              className="shrink-0 text-xs uppercase tracking-wide text-signal hover:text-signal-700 disabled:opacity-50"
            >
              Markér alle læst
            </button>
          )}
        </div>
      )}

      {loading && <p className="text-sm text-ink-500">Henter notifikationer…</p>}

      {!loading && items.length === 0 && (
        <p className="text-sm text-ink-500 border border-rule rounded-sm px-4 py-6 text-center">
          Du får besked her, når noget på din watchlist ændrer sig i CVR.
        </p>
      )}

      {!loading && items.length > 0 && (
        <ul className="divide-y divide-rule border-y border-rule">
          {sections.map((section) => (
            <React.Fragment key={section.id}>
              <li className="py-2 bg-paper-200/20 -mx-4 px-4">
                <h2 className="font-mono text-xs uppercase tracking-widest text-ink-400">
                  {section.label}
                </h2>
              </li>
              {section.items.map((n) => (
                <NotificationRow
                  key={n.id}
                  notification={n}
                  watchlistItem={
                    n.entityType && n.entityId
                      ? findWatchlistItem(
                          watchlistData,
                          n.entityType as WatchlistEntityType,
                          n.entityId
                        )
                      : undefined
                  }
                  onMarkRead={() => void handleMarkRead(n)}
                  onRemove={() => void handleRemove(n)}
                  onOpenSettings={(item) => setSettingsItem(item)}
                />
              ))}
            </React.Fragment>
          ))}
        </ul>
      )}

      {settingsItem?.notificationPreferences && (
        <WatchlistItemNotificationModal
          open
          onClose={() => setSettingsItem(null)}
          displayName={settingsItem.displayName || settingsItem.entityId}
          entityType={settingsItem.entityType}
          preferences={settingsItem.notificationPreferences}
          showGlobalLink
          onSave={async (updates) => {
            try {
              const updated = await updateItemNotificationPreferences(
                settingsItem.id,
                updates
              )
              setSettingsItem(updated)
              showToast('Notifikationer opdateret.')
              return updated.notificationPreferences ?? { types: [] }
            } catch (err: unknown) {
              showToast(err instanceof Error ? err.message : 'Fejl', { tone: 'signal' })
              throw err
            }
          }}
        />
      )}
    </>
  )

  if (embedded) return content

  return (
    <Container width="prose" className="py-8">
      {content}
    </Container>
  )
}

function NotificationRow({
  notification: n,
  watchlistItem,
  onMarkRead,
  onRemove,
  onOpenSettings,
}: {
  notification: AppNotification
  watchlistItem?: WatchlistItem
  onMarkRead: () => void
  onRemove: () => void
  onOpenSettings: (item: WatchlistItem) => void
}) {
  const navigate = useNavigate()
  const href = notificationHref(n)
  const unread = isUnread(n)
  const entityLabel = useMemo(
    () => notificationEntityLabel(n, watchlistItem),
    [n, watchlistItem]
  )
  const canOpenEntitySettings = Boolean(
    watchlistItem?.notificationPreferences && n.entityType && n.entityId
  )

  const title = href ? (
    <Link
      to={href}
      onClick={onMarkRead}
      className={`${unread ? 'font-semibold text-ink' : 'font-normal text-ink-600'} font-serif text-base hover:text-signal transition-colors block`}
    >
      {n.title}
    </Link>
  ) : (
    <p className={unread ? 'font-serif text-base font-semibold text-ink' : 'font-serif text-base font-normal text-ink-600'}>
      {n.title}
    </p>
  )

  return (
    <li className="group py-3">
      <div className="flex items-start justify-between gap-3">
        <div className="flex-1 min-w-0">
          <NotificationItemContent notification={n} title={title} />
        </div>

        <div
          className={`flex shrink-0 items-center gap-0.5 transition-opacity ${
            unread
              ? 'opacity-100 sm:opacity-0 sm:group-hover:opacity-100 sm:group-focus-within:opacity-100'
              : 'opacity-0 group-hover:opacity-100 group-focus-within:opacity-100'
          }`}
        >
          {unread && (
            <button
              type="button"
              onClick={onMarkRead}
              aria-label="Markér som læst"
              title="Markér som læst"
              className="inline-flex items-center justify-center p-1.5 text-ink-500 hover:text-ink transition-colors rounded-sm"
            >
              <Check className="h-4 w-4" aria-hidden />
            </button>
          )}

          <Menu
            dense
            trigger={<MoreHorizontal className="h-4 w-4" aria-hidden />}
            triggerAriaLabel="Notifikationshandlinger"
            align="right"
            triggerClassName="p-1.5"
          >
            {(close) => (
              <>
                <MenuItem
                  danger
                  icon={<Trash2 className="h-3.5 w-3.5" />}
                  onClick={() => {
                    close()
                    onRemove()
                  }}
                >
                  Fjern fra indbakke
                </MenuItem>
                <MenuSeparator />
                <MenuItem
                  icon={<Bell className="h-3.5 w-3.5" />}
                  disabled={!canOpenEntitySettings}
                  onClick={() => {
                    if (!watchlistItem) return
                    close()
                    onOpenSettings(watchlistItem)
                  }}
                >
                  Indstillinger for {entityLabel}
                </MenuItem>
                <MenuItem
                  icon={<Settings className="h-3.5 w-3.5" />}
                  onClick={() => {
                    close()
                    navigate('/account/notifikationer/indstillinger')
                  }}
                >
                  Globale indstillinger
                </MenuItem>
              </>
            )}
          </Menu>
        </div>
      </div>
    </li>
  )
}

export default NotificationsInbox
